/**
 * DanaVoice - Authentication Context
 *
 * React Context for managing authentication state across the application.
 * Uses Stack Auth (Neon Auth) for authentication.
 * Provides user info, login/logout functions.
 */

import React, { createContext } from 'react';
import { useUser } from '@stackframe/react';
import { stackClientApp } from '../stack';
import type { User } from '@stackframe/react';

/**
 * Authentication Context Value
 */
export interface AuthContextValue {
  user: User | null | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (redirectUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

/**
 * Authentication Context
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider
 *
 * Manages authentication state using Stack Auth.
 * Wraps Stack Auth hooks and provides unified interface.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // Use Stack Auth's useUser hook (no redirect - just check auth state)
  const user = useUser();
  
  /**
   * Login
   *
   * Redirects to Stack Auth sign-in page.
   */
  const login = async (redirectUrl: string = '/dashboard') => {
    // Stack Auth handles login via the /handler/sign-in route
    window.location.href = `/handler/sign-in?after=${encodeURIComponent(redirectUrl)}`;
  };

  /**
   * Logout
   *
   * Signs out the current user using Stack Auth.
   */
  const logout = async () => {
    await stackClientApp.signOut();
    window.location.href = '/handler/sign-in';
  };

  /**
   * Refresh Authentication
   *
   * Stack Auth automatically handles token refresh.
   * This is a no-op for compatibility with existing code.
   */
  const refreshAuth = async () => {
    // Stack Auth handles refresh automatically
    // No-op for backward compatibility
  };

  /**
   * Context value
   */
  const value: AuthContextValue = {
    user: user ?? null,
    isLoading: user === undefined,
    isAuthenticated: user !== null && user !== undefined,
    login,
    logout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 *
 * Custom hook to access authentication context.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}